import React from "react";
import { Calendar, Repeat, Sparkles, Check } from "lucide-react";

const PLAN = {
  name: "Maintenance Plan",
  price: 100,
  frequency: "/month",
  blurb: "Keep your car looking detailed all year round — without thinking about it.",
  perks: [
    "2 full details every month (interior + exterior)",
    "Priority booking — first pick of slots",
    "10% off any add-ons (clay bar, engine bay, etc.)",
    "Cancel anytime, no contract",
  ],
};

const MAILTO = `mailto:stautocarecontact@gmail.com?subject=${encodeURIComponent(
  "STAC Maintenance Plan enquiry"
)}&body=${encodeURIComponent(
  "Hi STAC,\n\nI'd like to find out more about the monthly Maintenance Plan.\n\nName:\nVehicle:\nLocation:\n\nThanks!"
)}`;

export default function MaintenancePlan() {
  return (
    <section
      id="plan"
      data-testid="maintenance-plan-section"
      className="px-6 md:px-12 lg:px-20 py-28 md:py-36 bg-[#0A0A0A] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: pitch */}
          <div className="lg:col-span-5">
            <div className="overline mb-5">— Coming soon</div>
            <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] mb-6">
              Looking like new,
              <br />
              <span className="text-white/55">on autopilot.</span>
            </h2>
            <p className="text-white/65 leading-relaxed text-base max-w-md">
              {PLAN.blurb} Detailing isn&apos;t a one-time thing — the cars that always look immaculate
              are the ones with a regular routine.
            </p>

            <div className="mt-10 flex flex-wrap gap-6 text-sm text-white/55">
              <div className="flex items-center gap-2">
                <Repeat size={14} /> Recurring
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} /> Flexible scheduling
              </div>
              <div className="flex items-center gap-2">
                <Sparkles size={14} /> Best value
              </div>
            </div>
          </div>

          {/* Right: plan card */}
          <div className="lg:col-span-6 lg:col-start-7">
            <article
              data-testid="maintenance-plan-card"
              className="relative bg-[#0E0E0E] border border-white/10 p-8 lg:p-12"
            >
              <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-black bg-white px-2.5 py-1">
                <Sparkles size={11} /> Best value
              </span>

              <div className="overline mb-3">— Subscription</div>
              <h3 className="font-display text-3xl font-medium mb-3">{PLAN.name}</h3>

              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-6xl font-light">£{PLAN.price}</span>
                <span className="text-sm text-white/55">{PLAN.frequency}</span>
              </div>
              <p className="text-xs text-white/45 tracking-wide mb-10">
                Indicative price — final pricing confirmed at enquiry.
              </p>

              <ul className="space-y-3 mb-10">
                {PLAN.perks.map((perk) => (
                  <li
                    key={perk}
                    className="flex items-start gap-3 text-sm text-white/80 leading-snug"
                  >
                    <Check size={14} className="mt-1 text-white/50 flex-shrink-0" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-white/10">
                <a
                  href={MAILTO}
                  data-testid="maintenance-plan-cta"
                  className="flex-1 inline-flex items-center justify-center bg-white text-black px-7 py-4 text-xs font-medium tracking-[0.22em] uppercase hover:bg-white/85 transition-colors"
                >
                  Enquire now
                </a>
                <a
                  href="#book"
                  data-testid="maintenance-plan-secondary"
                  className="flex-1 inline-flex items-center justify-center border border-white/20 text-white hover:border-white/60 px-7 py-4 text-xs font-medium tracking-[0.22em] uppercase transition-colors"
                >
                  Try a one-off first
                </a>
              </div>

              <p className="mt-6 text-xs text-white/40 text-center">
                Enquiries go straight to STAC — usually replied within a few hours.
              </p>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
